// src/lib/bootCoordinator.ts
import { onAuthStateChanged } from "firebase/auth";
import { auth, getAuthReadyUser } from "./firebase";
import { getActivePhone, getAllProfiles } from "./profileStore";
import { getHasAccount } from "./persistedAccount";

export type BootState = { ready: boolean; authed: boolean; hasAccount: boolean };

let bootCache: BootState | null = null;
let bootPromise: Promise<BootState> | null = null;
let wired = false;
let lastAuthUid: string | null = null;

function notifyInvalidated() {
  try {
    window.dispatchEvent(new Event("bw:boot:invalidated"));
  } catch {}
}

export function invalidateBootState() {
  bootCache = null;
  bootPromise = null;
  notifyInvalidated();
}

function ensureAuthWired() {
  if (wired) return;
  wired = true;
  onAuthStateChanged(auth, (user) => {
    const uid = user?.uid || null;
    if (uid !== lastAuthUid) {
      lastAuthUid = uid;
      invalidateBootState();
    }
  });
}

async function computeBootState(): Promise<BootState> {
  let authed = false;
  let phone: string | null = null;
  try {
    const liveUser = auth.currentUser;
    const user = liveUser || (await getAuthReadyUser());
    authed = Boolean(user);
    phone = user?.phoneNumber || null;
  } catch {
    authed = false;
  }

  try {
    const localPhone = getActivePhone();
    if (!phone && localPhone) phone = localPhone;
  } catch {}

  try {
    if (!phone) {
      const fallback =
        localStorage.getItem("bw.active.phone") ||
        localStorage.getItem("bw.lastPhone") ||
        localStorage.getItem("bw:lastUserPhone");
      phone = fallback || null;
    }
  } catch {}

  let hasAccount = Boolean(phone);
  if (!hasAccount) {
    try {
      hasAccount = getAllProfiles().length > 0;
    } catch {}
  }

  if (!hasAccount) {
    try {
      const idbHasAccount = await getHasAccount();
      hasAccount = hasAccount || idbHasAccount;
    } catch {}
  }

  if (!hasAccount) {
    try {
      hasAccount = localStorage.getItem("bw.hasAccount") === "1";
    } catch {}
  }

  if (authed) {
    try {
      if (phone) {
        sessionStorage.setItem("bw.session.phoneVerified", phone);
        sessionStorage.setItem("bw.session.phone", phone);
        localStorage.setItem("bw.active.phone", phone);
      }
      sessionStorage.setItem("bw.auth.verified", "1");
    } catch {}
  }

  return { ready: true, authed, hasAccount };
}

export async function getBootState(): Promise<BootState> {
  ensureAuthWired();
  if (bootCache) return bootCache;
  if (!bootPromise) {
    bootPromise = computeBootState().then((next) => {
      bootCache = next;
      return next;
    });
  }
  return bootPromise;
}

export function getBootCache(): BootState | null {
  return bootCache;
}
